import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { NO_RESET_WHITE_LIST, PERMISSION_CODES as P } from '@/constants'

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: 'router.login',
      noTagsView: true
    }
  },
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: 'router.dashboard',
      icon: 'mdi:view-dashboard'
    },
    children: [
      {
        path: 'analysis',
        name: 'DashboardAnalysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        meta: {
          title: 'router.dashboard',
          icon: 'mdi:view-dashboard',
          noCache: true,
          affix: true
        }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    redirect: '/system/users',
    name: 'System',
    meta: {
      title: 'router.system',
      icon: 'mdi:cog-outline',
      // 父级：只要用户持有 user/permission/role 任一读权限即可看到该一级菜单
      permission: [P.USER_READ, P.PERMISSION_READ, P.ROLE_READ]
    },
    children: [
      {
        path: 'users',
        name: 'SystemUsers',
        component: () => import('@/views/System/Users.vue'),
        meta: {
          title: 'router.systemUsers',
          icon: 'mdi:account-group',
          permission: [P.USER_READ, P.USER_MANAGE]
        }
      },
      {
        path: 'roles',
        name: 'SystemRoles',
        component: () => import('@/views/System/Roles.vue'),
        meta: {
          title: 'router.systemRoles',
          icon: 'mdi:shield-account',
          permission: [P.ROLE_READ, P.ROLE_MANAGE]
        }
      },
      {
        path: 'permissions',
        name: 'SystemPermissions',
        component: () => import('@/views/System/Permissions.vue'),
        meta: {
          title: 'router.systemPermissions',
          icon: 'mdi:key-variant',
          permission: [P.PERMISSION_READ, P.PERMISSION_MANAGE]
        }
      }
    ]
  },
  {
    path: '/teaching',
    component: Layout,
    redirect: '/teaching/courses',
    name: 'Teaching',
    meta: {
      title: 'router.teaching',
      icon: 'mdi:school-outline',
      // 父级：持有 course 或 skill 任一读权限即可看到「教学管理」一级菜单
      permission: [P.COURSE_READ, P.COURSE_MANAGE, P.SKILL_READ, P.SKILL_MANAGE]
    },
    children: [
      {
        path: 'courses',
        name: 'TeachingCourses',
        component: () => import('@/views/Teaching/Courses.vue'),
        meta: {
          title: 'router.teachingCourses',
          icon: 'mdi:book-multiple',
          permission: [P.COURSE_READ, P.COURSE_MANAGE]
        }
      },
      {
        path: 'skills',
        name: 'TeachingSkills',
        component: () => import('@/views/Teaching/Skills.vue'),
        meta: {
          title: 'router.teachingSkills',
          icon: 'mdi:lightbulb-on-outline',
          permission: [P.SKILL_READ, P.SKILL_MANAGE]
        }
      },
      {
        path: 'assets',
        name: 'TeachingAssets',
        component: () => import('@/views/Teaching/Assets.vue'),
        meta: {
          title: 'router.teachingAssets',
          icon: 'mdi:file-document-multiple-outline',
          permission: [P.ASSET_READ, P.ASSET_MANAGE]
        }
      }
    ]
  },
  {
    path: '/qa',
    component: Layout,
    redirect: '/qa/records',
    name: 'Qa',
    meta: {
      title: 'router.qa',
      icon: 'mdi:comment-question-outline',
      permission: [P.QA_READ]
    },
    children: [
      {
        path: 'records',
        name: 'QaRecords',
        component: () => import('@/views/Qa/Records.vue'),
        meta: {
          title: 'router.qaRecords',
          icon: 'mdi:message-text-outline',
          permission: [P.QA_READ]
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/views/Error/404.vue'),
    name: 'Fallback',
    meta: {
      hidden: true,
      breadcrumb: false,
      noTagsView: true
    }
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string) && router.hasRoute(name)) {
      router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
